import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import TabCodeExamples from '../../../components/docs/TabCodeExamples';
import { 
  pythonAuthExample, 
  nodeAuthExample, 
  javaAuthExample, 
  goAuthExample, 
  csharpAuthExample,
  rustAuthExample
} from '../../../examples/codeSnippets';

// Code block component for syntax highlighting
const CodeBlock: React.FC<{ code: string; language?: string }> = ({ code, language = 'bash' }) => {
  return (
    <div className="bg-gray-900 rounded-md p-4 my-4 overflow-x-auto">
      <pre className="text-sm text-gray-300 font-mono">
        <code>{code}</code>
      </pre>
    </div>
  );
};

// Step card component
const StepCard: React.FC<{ number: number; title: string; children: React.ReactNode }> = ({ 
  number, 
  title, 
  children 
}) => {
  return (
    <div className="border border-border rounded-lg mb-8 overflow-hidden">
      <div className="flex items-center p-4 bg-background-alt border-b border-border">
        <div className="bg-primary text-white w-10 h-10 rounded-full flex items-center justify-center mr-4 font-bold">
          {number}
        </div>
        <h3 className="text-xl font-semibold text-primary">{title}</h3>
      </div>
      <div className="p-6">
        {children}
      </div>
    </div>
  );
};

const IntegrationGuidePage: React.FC = () => {
  return (
    <div className="container-custom py-16">
      <Helmet>
        <title>ReasonShield Vendor Integration Guide | API Integration</title>
        <meta 
          name="description" 
          content="Step-by-step guide for integrating with the ReasonShield Vendor API. Learn how to set up your integration, authenticate requests, and process data." 
        />
      </Helmet>
      
      <h1 className="text-4xl md:text-5xl font-bold mb-4">Vendor Integration Guide</h1>
      <p className="text-xl mb-8 text-text">
        Follow this step-by-step guide to integrate your laboratory information system with ReasonShield.
      </p>
      
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-12">
        <div className="md:col-span-1 space-y-6">
          <div className="bg-background-alt p-6 rounded-lg sticky top-24">
            <h3 className="text-lg font-semibold mb-4 text-primary">Contents</h3>
            <nav>
              <ul className="space-y-2">
                <li>
                  <a href="#introduction" className="text-text hover:text-primary block py-1">Introduction</a>
                </li>
                <li>
                  <a href="#prerequisites" className="text-text hover:text-primary block py-1">Prerequisites</a>
                </li>
                <li>
                  <a href="#step-1" className="text-text hover:text-primary block py-1">Step 1: Register as a Vendor</a>
                </li>
                <li>
                  <a href="#step-2" className="text-text hover:text-primary block py-1">Step 2: Set Up Authentication</a>
                </li>
                <li>
                  <a href="#step-3" className="text-text hover:text-primary block py-1">Step 3: Test API Connection</a>
                </li>
                <li>
                  <a href="#step-4" className="text-text hover:text-primary block py-1">Step 4: Implement Data Submission</a>
                </li>
                <li>
                  <a href="#step-5" className="text-text hover:text-primary block py-1">Step 5: Handle Reports</a>
                </li>
                <li>
                  <a href="#step-6" className="text-text hover:text-primary block py-1">Step 6: Go Live</a>
                </li>
                <li>
                  <a href="#code-examples" className="text-text hover:text-primary block py-1">Code Examples</a>
                </li>
                <li>
                  <a href="#faqs" className="text-text hover:text-primary block py-1">FAQs</a>
                </li>
                <li>
                  <a href="#support" className="text-text hover:text-primary block py-1">Support</a>
                </li>
              </ul>
            </nav>
          </div>
        </div>
        
        <div className="md:col-span-3">
          <section id="introduction" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-primary">Introduction</h2>
            <div className="bg-background-alt p-6 rounded-lg">
              <p className="mb-4">
                The ReasonShield Vendor API allows laboratory information systems to submit test data for analysis and receive detailed reports. This guide will walk you through the integration process from registration to deployment.
              </p>
              <p className="mb-4">
                Our API follows RESTful principles and uses JSON for data exchange. All endpoints use HTTPS for security, and authentication is required for all requests.
              </p>
              <div className="flex flex-col md:flex-row gap-4 mt-6">
                <Link to="/vendor/docs/api-documentation" className="btn btn-primary">
                  View API Documentation
                </Link>
                <a href="https://app.reasonshield.com/vendor-portal" className="btn btn-secondary" target="_blank" rel="noopener noreferrer">
                  Access Vendor Portal
                </a>
              </div>
            </div>
          </section>
          
          <section id="prerequisites" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-primary">Prerequisites</h2>
            <div className="bg-background-alt p-6 rounded-lg">
              <p className="mb-4">
                Before starting the integration process, you'll need:
              </p>
              <ul className="list-disc ml-6 space-y-2">
                <li>An active ReasonShield vendor account</li>
                <li>Basic knowledge of RESTful APIs and JSON</li>
                <li>A secure server environment for making API calls</li>
                <li>Ability to store and manage API credentials securely</li>
                <li>Understanding of your laboratory data structure</li>
              </ul>
            </div>
          </section>
          
          <StepCard number={1} title="Register as a Vendor">
            <p className="mb-4">
              To begin, you need to register as a vendor with ReasonShield.
            </p>
            <ol className="list-decimal ml-6 space-y-2 mb-4">
              <li>Contact our sales team at <a href="mailto:partnerships@reasonshield.com" className="text-primary hover:underline">partnerships@reasonshield.com</a></li>
              <li>Complete the vendor onboarding form</li>
              <li>Sign the necessary legal agreements</li>
              <li>Receive your vendor credentials (Vendor ID and initial API key)</li>
            </ol>
            <div className="bg-blue-50 p-4 rounded-md border border-blue-200 mb-4">
              <h4 className="font-semibold text-blue-700 mb-2">Note</h4>
              <p className="text-blue-700">
                The vendor registration process typically takes 1-3 business days to complete.
              </p>
            </div>
          </StepCard>
          
          <StepCard number={2} title="Set Up Authentication">
            <p className="mb-4">
              ReasonShield implements a dual authentication system that requires <strong>both API key and signature verification</strong> for all interactions, providing enhanced security for your sensitive data.
            </p>
            
            <div className="bg-background-alt bg-opacity-10 p-5 rounded-md border border-primary mb-6">
              <h4 className="font-semibold text-lg mb-2 text-primary">Required: Dual Authentication</h4>
              <p className="mb-3">
                Unlike many APIs that offer authentication options, ReasonShield requires both methods to be implemented together:
              </p>
              <ul className="list-disc text-primary ml-5 space-y-1">
                <li><strong>API Key:</strong> Identifies who you are (authentication)</li>
                <li><strong>Request Signature:</strong> Verifies the integrity of your data (verification)</li>
              </ul>
              <p className="mt-3 font-semibold">
                Both methods must be implemented for every API request, regardless of the endpoint or operation.
              </p>
            </div>
            
            <div className="border-t border-border pt-6 mt-6">
              <h4 className="font-semibold text-lg mb-4">Step 1: Implement API Key Authentication</h4>
              <p className="mb-4">
                The API key establishes your identity and must be included in the Authorization header:
              </p>
              <CodeBlock code={`Authorization: Bearer your-api-key-here`} language="bash" />
              
              <div className="mt-4 mb-6">
                <h5 className="font-semibold mb-2">API Key Implementation Example</h5>
                <CodeBlock code={`import requests
import os

# Get API key from environment variable (recommended)
API_KEY = os.getenv("VENDOR_API_KEY")

def make_request(endpoint, method="GET", data=None):
    """
    Make an authenticated request to the ReasonShield API
    (Note: This is incomplete - signature is also required)
    """
    base_url = "https://app.reasonshield.com/api/v1"
    url = f"{base_url}{endpoint}"
    
    # Set up headers with API key authentication
    headers = {
        "Content-Type": "application/json",
        "Authorization": f"Bearer {API_KEY}"
        # The X-Vendor-Signature header will also be required
    }
    
    # Make the request
    if method.upper() == "GET":
        response = requests.get(url, headers=headers)
    elif method.upper() == "POST":
        response = requests.post(url, json=data, headers=headers)
    
    return response
`} language="python" />
              </div>
            </div>
            
            <div className="border-t border-border pt-6 mt-6">
              <h4 className="font-semibold text-lg mb-4">Step 2: Implement Signature Verification</h4>
              <p className="mb-4">
                For every request, you must also generate and include a cryptographic signature of your payload:
              </p>
              <CodeBlock code={`import hmac
import hashlib
import json

def generate_signature(payload, secret):
    # Convert payload to JSON string and then to bytes
    payload_bytes = json.dumps(payload, separators=(',', ':'), sort_keys=True).encode('utf-8')
    
    # Create HMAC-SHA256 signature using the webhook secret
    signature = hmac.new(
        secret.encode('utf-8'),
        payload_bytes,
        hashlib.sha256
    ).hexdigest()
    
    return signature`} language="python" />
            </div>
            
            <div className="border-t border-border pt-6 mt-6">
              <h4 className="font-semibold text-lg mb-4">Complete Implementation Example</h4>
              <p className="mb-4">
                Here's a complete example showing both authentication methods used together:
              </p>
              <CodeBlock code={`import requests
import json
import hmac
import hashlib
import os
from datetime import datetime
import uuid

# Get credentials from environment variables
API_KEY = os.getenv("VENDOR_API_KEY")
WEBHOOK_SECRET = os.getenv("VENDOR_WEBHOOK_SECRET")
VENDOR_ID = os.getenv("VENDOR_ID")

def make_authenticated_request(endpoint, method="GET", payload=None):
    """
    Make a fully authenticated request using both API key and signature
    
    Args:
        endpoint: API endpoint path
        method: HTTP method (GET, POST, etc.)
        payload: Request payload for POST/PUT
        
    Returns:
        Response from API
    """
    base_url = "https://app.reasonshield.com/api/v1"
    url = f"{base_url}{endpoint}"
    
    # For POST/PUT requests with a payload
    if payload is not None:
        # Ensure we have vendor_id and timestamp
        if isinstance(payload, dict):
            if "vendor_id" not in payload:
                payload["vendor_id"] = VENDOR_ID
            if "timestamp" not in payload:
                payload["timestamp"] = datetime.now().isoformat()
            if "request_id" not in payload:
                payload["request_id"] = str(uuid.uuid4())
        
        # Generate signature for the payload
        signature = generate_signature(payload, WEBHOOK_SECRET)
        
        # Set up headers with BOTH authentication methods
        headers = {
            "Content-Type": "application/json",
            "Authorization": f"Bearer {API_KEY}",
            "X-Vendor-Signature": signature
        }
        
        # Make the request
        response = requests.request(
            method=method,
            url=url,
            json=payload,
            headers=headers
        )
    else:
        # For GET requests or requests without a payload
        headers = {
            "Authorization": f"Bearer {API_KEY}"
        }
        response = requests.request(
            method=method,
            url=url,
            headers=headers
        )
    
    # Check response
    if response.status_code in (200, 201, 202):
        return response.json()
    else:
        raise Exception(f"API request failed: {response.status_code} - {response.text}")

def generate_signature(payload, secret):
    """
    Generate HMAC signature for the payload
    """
    # Convert payload to JSON string with consistent ordering
    payload_string = json.dumps(payload, separators=(',', ':'), sort_keys=True)
    payload_bytes = payload_string.encode('utf-8')
    
    # Create signature
    signature = hmac.new(
        secret.encode('utf-8'),
        payload_bytes,
        hashlib.sha256
    ).hexdigest()
    
    return signature

# Example usage
try:
    # Submit test data
    test_data = {
        "test_data": {
            "test_id": "12345",
            "panel_type": "RESPIRATORY",
            # ... other test data fields
        }
    }
    
    result = make_authenticated_request(
        endpoint="/lis-webhook/vendor/submit-data",
        method="POST",
        payload=test_data
    )
    
    print(f"Data submitted successfully. ID: {result.get('data_id')}")
    
except Exception as e:
    print(f"Error: {str(e)}")
`} language="python" />
            </div>
            
            <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200 mt-6">
              <h4 className="font-semibold text-yellow-700 mb-2">Important Security Considerations</h4>
              <ul className="list-disc ml-6 space-y-1 text-yellow-700">
                <li>Both authentication components are mandatory for all requests</li>
                <li>Store API keys and secrets securely using environment variables or a secrets manager</li>
                <li>Ensure consistent JSON serialization when generating signatures</li>
                <li>Use the same field ordering for both request creation and signature generation</li>
                <li>Regenerate signatures for each request - never reuse signatures</li>
                <li>Implement proper error handling for authentication failures</li>
                <li>Rotate keys periodically according to your security policy</li>
              </ul>
            </div>
          </StepCard>
          
          <StepCard number={3} title="Test API Connection">
            <p className="mb-4">
              Before implementing the full integration, verify your connection to the API:
            </p>
            <CodeBlock code={`import requests
import os

API_BASE_URL = "https://app.reasonshield.com/api/v1"
API_ENDPOINT_PATH = "/lis-webhook/vendor"
API_KEY = os.getenv("VENDOR_API_KEY")

# Test the API connection
def test_connection():
    headers = {
        "Content-Type": "application/json",
        "Authorization": f"Bearer {API_KEY}"
    }
    
    url = f"{API_BASE_URL}{API_ENDPOINT_PATH}/ping"
    try:
        response = requests.get(url, headers=headers)
        
        if response.status_code == 200:
            print("Connection successful!")
            print(f"Response: {response.json()}")
            return True
        else:
            print(f"Connection failed. Status code: {response.status_code}")
            print(f"Response: {response.text}")
            return False
    
    except Exception as e:
        print(f"Exception occurred: {str(e)}")
        return False

# Run the test
test_connection()`} language="python" />
          
            <div className="bg-green-50 p-4 rounded-md border border-green-200 mt-4">
              <h4 className="font-semibold text-green-700 mb-2">Expected Response</h4>
              <CodeBlock code={`{
  "status": "ok",
  "message": "API connection successful",
  "vendor_id": "your-vendor-id",
  "timestamp": "2024-11-10T15:30:05Z"
}`} language="json" />
            </div>
          </StepCard>
          
          <StepCard number={4} title="Implement Data Submission">
            <p className="mb-4">
              Now, implement the data submission workflow:
            </p>
            
            <ol className="list-decimal ml-6 space-y-2 mb-4">
              <li>Format your laboratory test data according to the API specifications</li>
              <li>Submit the data to the API</li>
              <li>Store the returned data_id for future reference</li>
              <li>Implement error handling for failed submissions</li>
            </ol>
            
            <h4 className="font-semibold text-lg mb-2 mt-4">Sample Implementation</h4>
            <CodeBlock code={`import requests
import json
import os
import uuid
from datetime import datetime

API_BASE_URL = "https://app.reasonshield.com/api/v1"
API_ENDPOINT_PATH = "/lis-webhook/vendor"
VENDOR_ID = os.getenv("VENDOR_ID")
API_KEY = os.getenv("VENDOR_API_KEY")

def submit_test_data(test_data):
    """
    Submit test data to the ReasonShield API
    
    Args:
        test_data: Dictionary containing test data
        
    Returns:
        Dictionary containing the API response
    """
    try:
        headers = {
            "Content-Type": "application/json",
            "Authorization": f"Bearer {API_KEY}"
        }
        
        # Prepare the payload
        payload = {
            "vendor_id": VENDOR_ID,
            "timestamp": datetime.now().isoformat(),
            "request_id": str(uuid.uuid4()),
            "test_data": test_data
        }
        
        # Send the request
        url = f"{API_BASE_URL}{API_ENDPOINT_PATH}/submit-data"
        response = requests.post(url, json=payload, headers=headers)
        
        # Check for success
        if response.status_code == 202:
            result = response.json()
            print(f"Data submitted successfully. data_id: {result.get('data_id')}")
            return result
        else:
            print(f"Error submitting data. Status code: {response.status_code}")
            print(f"Response: {response.text}")
            raise Exception(f"Error submitting data: {response.text}")
            
    except Exception as e:
        print(f"Exception during data submission: {str(e)}")
        raise`} language="python" />
          </StepCard>
          
          <StepCard number={5} title="Handle Reports">
            <p className="mb-4">
              After submitting data, you need to:
            </p>
            
            <ol className="list-decimal ml-6 space-y-2 mb-4">
              <li>Check processing status until completion</li>
              <li>Retrieve the generated report</li>
              <li>Process the report data as needed for your system</li>
              <li>Optionally, download PDF reports</li>
            </ol>
            
            <h4 className="font-semibold text-lg mb-2 mt-4">Implementation Example</h4>
            <CodeBlock code={`import requests
import time
import os

API_BASE_URL = "https://app.reasonshield.com/api/v1"
API_ENDPOINT_PATH = "/lis-webhook/vendor"
API_KEY = os.getenv("VENDOR_API_KEY")
MAX_RETRIES = 30
RETRY_DELAY = 2  # seconds

def check_status(data_id):
    """
    Check the processing status of submitted data
    
    Args:
        data_id: The ID of the submitted data
        
    Returns:
        Dictionary containing the status information
    """
    headers = {
        "Authorization": f"Bearer {API_KEY}"
    }
    
    url = f"{API_BASE_URL}{API_ENDPOINT_PATH}/status/{data_id}"
    
    try:
        response = requests.get(url, headers=headers)
        
        if response.status_code == 200:
            status_data = response.json()
            print(f"Status check successful. Status: {status_data.get('status', 'unknown')}")
            return status_data
        else:
            print(f"Error checking status. Status code: {response.status_code}")
            print(f"Response: {response.text}")
            raise Exception(f"Error checking status: {response.text}")
    
    except Exception as e:
        print(f"Exception during status check: {str(e)}")
        raise

def get_report(data_id):
    """
    Retrieve the report for processed data
    
    Args:
        data_id: The ID of the processed data
        
    Returns:
        Dictionary containing the report data
    """
    headers = {
        "Authorization": f"Bearer {API_KEY}"
    }
    
    url = f"{API_BASE_URL}{API_ENDPOINT_PATH}/report/{data_id}"
    
    try:
        response = requests.get(url, headers=headers)
        
        if response.status_code == 200:
            report_data = response.json()
            print(f"Report retrieved successfully for data_id: {data_id}")
            return report_data
        else:
            print(f"Error retrieving report. Status code: {response.status_code}")
            print(f"Response: {response.text}")
            raise Exception(f"Error retrieving report: {response.text}")
    
    except Exception as e:
        print(f"Exception during report retrieval: {str(e)}")
        raise

def download_pdf_report(data_id, save_path):
    """
    Download the PDF report
    
    Args:
        data_id: The ID of the processed data
        save_path: Path to save the PDF file
        
    Returns:
        Path to the saved PDF file
    """
    headers = {
        "Authorization": f"Bearer {API_KEY}"
    }
    
    url = f"{API_BASE_URL}{API_ENDPOINT_PATH}/pdf-report/{data_id}"
    
    try:
        response = requests.get(url, headers=headers)
        
        if response.status_code == 200:
            # Save the PDF file
            file_path = f"{save_path}/report_{data_id}.pdf"
            with open(file_path, 'wb') as f:
                f.write(response.content)
            
            print(f"PDF report downloaded to: {file_path}")
            return file_path
        else:
            print(f"Error downloading PDF report. Status code: {response.status_code}")
            print(f"Response: {response.text}")
            raise Exception(f"Error downloading PDF report: {response.text}")
    
    except Exception as e:
        print(f"Exception during PDF download: {str(e)}")
        raise

def wait_for_processing(data_id):
    """
    Wait for data processing to complete
    
    Args:
        data_id: The ID of the submitted data
        
    Returns:
        Dictionary containing the final status information
    """
    for i in range(MAX_RETRIES):
        status_data = check_status(data_id)
        
        if status_data.get('status') == 'COMPLETED':
            print(f"Processing completed after {i+1} checks.")
            return status_data
        elif status_data.get('status') in ['FAILED', 'ERROR']:
            print(f"Processing failed: {status_data.get('message', 'Unknown error')}")
            raise Exception(f"Processing failed: {status_data.get('message', 'Unknown error')}")
        
        print(f"Processing in progress. Retry {i+1}/{MAX_RETRIES}. Waiting {RETRY_DELAY} seconds...")
        time.sleep(RETRY_DELAY)
    
    raise Exception(f"Processing timed out after {MAX_RETRIES} retries")`} language="python" />
          </StepCard>
          
          <StepCard number={6} title="Go Live">
            <p className="mb-4">
              Before moving to production:
            </p>
            
            <ol className="list-decimal ml-6 space-y-2 mb-6">
              <li>Thoroughly test your integration in the staging environment</li>
              <li>Implement logging and monitoring for API interactions</li>
              <li>Set up error notifications for failed requests</li>
              <li>Create documentation for your internal team</li>
              <li>Prepare a rollback plan in case of issues</li>
            </ol>
            
            <p className="mb-4">
              When you're ready to go live:
            </p>
            
            <ol className="list-decimal ml-6 space-y-2 mb-4">
              <li>Contact ReasonShield support to schedule your production launch</li>
              <li>Migrate to production API credentials</li>
              <li>Start with a small volume of data and gradually increase</li>
              <li>Monitor closely during the initial launch period</li>
            </ol>
            
            <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200 mt-4">
              <h4 className="font-semibold text-yellow-700 mb-2">Production Checklist</h4>
              <ul className="list-disc ml-6 space-y-1 text-yellow-700">
                <li>Set appropriate request timeouts</li>
                <li>Implement retry logic with exponential backoff</li>
                <li>Add circuit breakers for API outages</li>
                <li>Rate limit your API calls</li>
                <li>Validate data before submission</li>
                <li>Handle API rate limits gracefully</li>
              </ul>
            </div>
          </StepCard>
          
          <section id="code-examples" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-primary">Code Examples</h2>
            <div className="bg-background-alt p-6 rounded-lg">
              <p className="mb-4">
                We provide complete implementation examples in multiple programming languages to help you integrate with our API:
              </p>
              
              <TabCodeExamples
                title="Authentication Implementation"
                description="Here's how to implement our dual authentication system in different languages:"
                tabs={[
                  {
                    title: "Python Example",
                    language: "python",
                    code: pythonAuthExample,
                  },
                  {
                    title: "Node.js Example",
                    language: "javascript",
                    code: nodeAuthExample,
                  },
                  {
                    title: "Java Example",
                    language: "java",
                    code: javaAuthExample,
                  },
                  {
                    title: "Go Example",
                    language: "go",
                    code: goAuthExample,
                  },
                  {
                    title: "C# Example",
                    language: "csharp",
                    code: csharpAuthExample,
                  },
                  {
                    title: "Rust Example",
                    language: "rust",
                    code: rustAuthExample,
                  }
                ]}
              />
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                <a href="/examples/vendor_api_integration_example.py" target="_blank" rel="noopener noreferrer" className="block p-4 border border-border rounded-lg hover:border-primary hover:bg-background-alt transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">Python Full Example</span>
                    <span className="ml-auto">→</span>
                  </div>
                  <p className="text-sm text-text mt-2">Complete implementation with error handling and advanced features</p>
                </a>
                <a href="/examples/vendor_api_integration_example.go" target="_blank" rel="noopener noreferrer" className="block p-4 border border-border rounded-lg hover:border-primary hover:bg-background-alt transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">Go Full Example</span>
                    <span className="ml-auto">→</span>
                  </div>
                  <p className="text-sm text-text mt-2">Complete implementation with error handling and advanced features</p>
                </a>
                <a href="/examples/vendor_api_integration_example.rs" target="_blank" rel="noopener noreferrer" className="block p-4 border border-border rounded-lg hover:border-primary hover:bg-background-alt transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">Rust Full Example</span>
                    <span className="ml-auto">→</span>
                  </div>
                  <p className="text-sm text-text mt-2">Complete implementation with error handling and advanced features</p>
                </a>
                <a href="/examples/vendor_api_integration_example_java.java" target="_blank" rel="noopener noreferrer" className="block p-4 border border-border rounded-lg hover:border-primary hover:bg-background-alt transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">Java Full Example</span>
                    <span className="ml-auto">→</span>
                  </div>
                  <p className="text-sm text-text mt-2">Complete implementation with error handling and advanced features</p>
                </a>
                <a href="/examples/VendorApiIntegrationExample.cs" target="_blank" rel="noopener noreferrer" className="block p-4 border border-border rounded-lg hover:border-primary hover:bg-background-alt transition-colors duration-200">
                  <div className="flex items-center justify-between">
                    <span className="text-lg font-semibold">C# Full Example</span>
                    <span className="ml-auto">→</span>
                  </div>
                  <p className="text-sm text-text mt-2">Complete implementation with error handling and advanced features</p>
                </a>
              </div>
              
              <p className="text-center mt-6">
                <a href="https://github.com/reasonshield/api-examples" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                  View all examples on GitHub →
                </a>
              </p>
            </div>
          </section>
          
          <section id="faqs" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-primary">Frequently Asked Questions</h2>
            
            <div className="space-y-4">
              <div className="bg-background-alt p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-2 text-primary">How long does data processing take?</h3>
                <p>
                  Data processing typically takes 30-60 seconds but may take longer depending on the complexity of the test data. We recommend implementing asynchronous processing and status checking rather than waiting for immediate results.
                </p>
              </div>
              
              <div className="bg-background-alt p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-2 text-primary">What should I do if I receive an error?</h3>
                <p>
                  First, check the error message and HTTP status code returned by the API. Common issues include authentication problems, invalid data format, or rate limiting. If you can't resolve the issue, contact ReasonShield support with the error details and request ID.
                </p>
              </div>
              
              <div className="bg-background-alt p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-2 text-primary">Can I test the API before implementation?</h3>
                <p>
                  Yes! We provide a sandbox environment for testing your integration. Contact support to request sandbox credentials. The sandbox environment has the same API endpoints but uses test data and doesn't affect production systems.
                </p>
              </div>
              
              <div className="bg-background-alt p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-2 text-primary">How do I handle large volumes of data?</h3>
                <p>
                  For large volumes, we recommend:
                </p>
                <ul className="list-disc ml-6 mt-2">
                  <li>Using batch processing with reasonable batch sizes (50-100 records)</li>
                  <li>Implementing concurrent processing with rate limiting</li>
                  <li>Setting up a queue system for very high volumes</li>
                  <li>Contacting us to discuss custom rate limit increases for enterprise customers</li>
                </ul>
              </div>
            </div>
          </section>
          
          <section id="support" className="mb-12">
            <h2 className="text-3xl font-bold mb-6 text-primary">Support</h2>
            <div className="bg-background-alt p-6 rounded-lg">
              <p className="mb-4">
                Need help with your integration? Our support team is available to assist:
              </p>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
                <div className="border border-border p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-2 text-primary">Technical Support</h3>
                  <p className="mb-2">For API or integration issues:</p>
                  <a href="mailto:api-support@reasonshield.com" className="text-primary hover:underline block mb-1">api-support@reasonshield.com</a>
                  <p className="text-sm text-text">Response within 24 hours</p>
                </div>
                
                <div className="border border-border p-6 rounded-lg">
                  <h3 className="text-xl font-semibold mb-2 text-primary">Account Management</h3>
                  <p className="mb-2">For account or billing issues:</p>
                  <a href="mailto:accounts@reasonshield.com" className="text-primary hover:underline block mb-1">accounts@reasonshield.com</a>
                  <p className="text-sm text-text">Response within 48 hours</p>
                </div>
              </div>
              
              <p className="mb-4">
                When contacting support, please include:
              </p>
              
              <ul className="list-disc ml-6 mb-4">
                <li>Your vendor ID</li>
                <li>Detailed description of the issue</li>
                <li>Screenshots or logs if applicable</li>
                <li>Steps to reproduce the problem</li>
                <li>Any error messages or codes received</li>
              </ul>
              
              <div className="flex justify-center mt-6">
                <Link to="/vendor-support" className="btn btn-primary">
                  Submit Support Request
                </Link>
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default IntegrationGuidePage; 